import { Injectable } from '@angular/core';
import moment from 'moment';
import { RotaSummary } from '../../../classes/models/rota/RotaSummary';
import { DayOfWeek, dayOfWeekValues } from '../../../classes/enums/DayOfWeek';
import {
  RotaAssignmentResponse,
  RotaAvailabilityAssignmentResponse,
  RotaFullDayAbsenceAssignmentResponse,
  RotaHalfDayAbsenceAssignmentResponse,
  RotaRoleAssignmentResponse
} from '../../../interfaces/response/RotaResponses';
import { RotaRole } from '../../../classes/enums/RotaRole';

@Injectable({
  providedIn: 'root',
})
export class TestRotaDataService {

  /**
   * Gets rota summaries for a store between rota start date and rota end date.
   * @param storeId an identifier of a store.
   * @param from the earliest rota date.
   * @param to the latest rota date.
   */
  getRotaSummaries(storeId: string, from: string, to: string): RotaSummary[] {
    const rotaSummaries = [];
    for (let date = moment(from); date <= moment(to); date = moment(date).add(7, 'days')) {
      rotaSummaries.push(new RotaSummary(moment(date).format('YYYY-MM-DD')));
    }
    return rotaSummaries;
  }

  /**
   * Gets rota assignments test data.
   * @param date rota date.
   */
  getRotaAssignments(date: string): RotaAssignmentResponse[] {
    const assignments: RotaAssignmentResponse[] = [];

    [DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.SATURDAY].forEach(weekday => {
      assignments.push({
        employeeId: 'AAA', positionId: 'DFSAAA', dayOfWeek: weekday, startTime: '09:00:00', endTime: '14:00:00'
      });
    });

    dayOfWeekValues().forEach(weekday => {
      assignments.push({
        employeeId: 'BBB', positionId: 'DFSBBB', dayOfWeek: weekday, startTime: '09:00:00', endTime: '14:00:00'
      });
    });

    [DayOfWeek.MONDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SUNDAY].forEach(weekday => {
      assignments.push({
        employeeId: 'CCC', positionId: 'DFSCCC', dayOfWeek: weekday, startTime: '09:00:00', endTime: '14:00:00'
      });
    });

    [DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY].forEach(weekday => {
      assignments.push({
        employeeId: 'DDD', positionId: 'DFSDDD', dayOfWeek: weekday, startTime: '15:45:00', endTime: '20:00:00'
      });
    });
    assignments.push({
      employeeId: 'DDD', positionId: 'DFSDDD', dayOfWeek: DayOfWeek.SATURDAY, startTime: '09:00:00', endTime: '19:00:00'
    });

    [DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SUNDAY].forEach(weekday => {
      assignments.push({
        employeeId: 'EEE', positionId: 'DFSEEE', dayOfWeek: weekday, startTime: '15:00:00', endTime: '21:00:00'
      });
    });

    return assignments;
  }

  /**
   * Gets rota role assignments test data.
   * @param date rota date.
   */
  getRoleAssignments(date: string): RotaRoleAssignmentResponse[] {
    const assignments: RotaRoleAssignmentResponse[] = [];

    assignments.push({
      employeeId: 'EEE',
      positionId: 'DFSEEE',
      role: RotaRole.CHANGEOVER,
      dayOfWeek: DayOfWeek.THURSDAY,
      startTime: '16:00:00',
      endTime: '18:00:00'
    });

    [DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SUNDAY].forEach(weekday => {
      assignments.push({
        employeeId: 'BBB',
        positionId: 'DFSBBB',
        role: RotaRole.KEYHOLDER,
        dayOfWeek: weekday,
        startTime: '09:00:00',
        endTime: '09:15:00'
      });
      assignments.push({
        employeeId: 'EEE',
        positionId: 'DFSEEE',
        role: RotaRole.KEYHOLDER,
        dayOfWeek: weekday,
        startTime: '20:45:00',
        endTime: '21:0:00'
      });
    });
    assignments.push({
      employeeId: 'DDD',
      positionId: 'DFSDDD',
      role: RotaRole.KEYHOLDER,
      dayOfWeek: DayOfWeek.SATURDAY,
      startTime: '09:00:00',
      endTime: '09:15:00'
    });
    assignments.push({
      employeeId: 'DDD',
      positionId: 'DFSDDD',
      role: RotaRole.KEYHOLDER,
      dayOfWeek: DayOfWeek.SATURDAY,
      startTime: '18:45:00',
      endTime: '19:00:00'
    });

    return assignments;
  }

  /**
   * Gets rota availabilities test data.
   * @param date rota date.
   */
  getAvailabilityAssignments(date: string): RotaAvailabilityAssignmentResponse[] {
    const assignments: RotaAvailabilityAssignmentResponse[] = [];

    dayOfWeekValues().forEach(weekDay => {
      assignments.push({
        employeeId: 'AAA',
        positionId: 'DFSAAA',
        dayOfWeek: weekDay,
        startTime: '09:00:00',
        endTime: '14:00:00'
      });
      assignments.push({
        employeeId: 'BBB',
        positionId: 'DFSBBB',
        dayOfWeek: weekDay,
        startTime: '09:00:00',
        endTime: '14:00:00'
      });
      assignments.push({
        employeeId: 'CCC',
        positionId: 'DFSCCC',
        dayOfWeek: weekDay,
        startTime: '13:00:00',
        endTime: '20:00:00'
      });
      assignments.push({
        employeeId: 'DDD',
        positionId: 'DFSDDD',
        dayOfWeek: weekDay,
        startTime: '13:00:00',
        endTime: '20:00:00'
      });
      assignments.push({
        employeeId: 'EEE',
        positionId: 'DFSEEE',
        dayOfWeek: weekDay,
        startTime: '16:00:00',
        endTime: '20:00:00'
      });
    });

    return assignments;
  }

  /**
   * Gets full day rota absence test data.
   * @param date rota date.
   */
  getFullDayAbsenceAssignments(date: string): RotaFullDayAbsenceAssignmentResponse[] {
    const assignments: RotaFullDayAbsenceAssignmentResponse[] = [];

    assignments.push({
      employeeId: 'AAA',
      positionId: 'DFSAAA',
      dayOfWeek: DayOfWeek.THURSDAY
    });
    assignments.push({
      employeeId: 'AAA',
      positionId: 'DFSAAA',
      dayOfWeek: DayOfWeek.FRIDAY
    });
    assignments.push({
      employeeId: 'AAA',
      positionId: 'DFSAAA',
      dayOfWeek: DayOfWeek.SUNDAY
    });

    assignments.push({
      employeeId: 'DDD',
      positionId: 'DFSDDD',
      dayOfWeek: DayOfWeek.MONDAY
    });

    [DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY].forEach(weekDay => assignments.push({
      employeeId: 'FFF',
      positionId: 'DFSFFF',
      dayOfWeek: weekDay
    }));

    return assignments;
  }

  /**
   * Gets half day rota absence test data.
   * @param date rota date.
   */
  getHalfDayAbsenceAssignments(date: string): RotaHalfDayAbsenceAssignmentResponse[] {
    const assignments: RotaHalfDayAbsenceAssignmentResponse[] = [];

    assignments.push({
      employeeId: 'CCC',
      positionId: 'DFSCCC',
      dayOfWeek: DayOfWeek.FRIDAY,
      startTime: '14:00:00',
      endTime: '00:00:00'
    });

    return assignments;
  }
}
